import React, { useEffect, useRef } from 'react';
import './Home.css';

/* Scroll-fade: elemen muncul saat masuk viewport */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); io.unobserve(el); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

const STATS = [
  { v: '99%',  l: 'Akurasi Model',    s: '5-Fold Cross Validated' },
  { v: '100',  l: 'Fitur Klinis',     s: '8 kelompok terstruktur' },
  { v: '3',    l: 'Kategori Kejang',  s: 'Berdasarkan kondisi' },
  { v: '1200', l: 'Data Latih',       s: 'Sintetik klinis terkurasi' },
];

const TYPES = [
  {
    key: 'simple',
    color: 'var(--c-simple)',
    bg: 'rgba(91,155,201,0.10)',
    border: 'rgba(91,155,201,0.30)',
    hl: 'hl-mint',
    badge: 'Simple Febrile',
    title: 'Kejang Demam Sederhana',
    desc: 'Dipicu demam tinggi, berlangsung kurang dari 15 menit, bersifat umum, dan tidak berulang dalam 24 jam.',
    items: ['Suhu tubuh ≥ 38.5°C', 'Didahului demam', 'Durasi < 15 menit', 'Tidak fokal, tidak berulang'],
  },
  {
    key: 'complex',
    color: 'var(--c-complex)',
    bg: 'rgba(201,123,61,0.10)',
    border: 'rgba(201,123,61,0.30)',
    hl: 'hl-salmon',
    badge: 'Complex Febrile',
    title: 'Kejang Demam Kompleks',
    desc: 'Kejang demam dengan ciri: durasi ≥ 15 menit, gerakan fokal, atau berulang dalam 24 jam. Perlu evaluasi neurologis.',
    items: ['Durasi ≥ 15 menit', 'Gerakan fokal (satu sisi)', 'Berulang dalam 24 jam', 'Risiko epilepsi lebih tinggi'],
  },
  {
    key: 'afebrile',
    color: 'var(--c-afebrile)',
    bg: 'rgba(131,120,194,0.10)',
    border: 'rgba(131,120,194,0.30)',
    hl: 'hl-lavender',
    badge: 'Afebrile',
    title: 'Kejang Tanpa Demam',
    desc: 'Kejang yang terjadi tanpa didahului demam. Sering terkait epilepsi atau kelainan neurologis — perlu EEG dan MRI.',
    items: ['Suhu tubuh normal (< 38°C)', 'Tidak didahului demam', 'Bisa fokal atau umum', 'Terkait epilepsi / kelainan otak'],
  },
];

const FEATURES = [
  { n: '01', t: 'Berbasis Suhu & Demam', d: 'Input suhu tubuh, riwayat demam, dan durasi demam sebelum kejang untuk analisis akurat.' },
  { n: '02', t: 'Konteks Usia Anak',     d: 'Usia dalam bulan menjadi faktor kunci — profil kejang berbeda signifikan antar kelompok usia.' },
  { n: '03', t: 'Durasi & Pola Kejang',  d: 'Durasi, gerakan fokal, dan kejang berulang menentukan apakah kejang sederhana atau kompleks.' },
  { n: '04', t: 'Prediksi Real-time',    d: 'Hasil klasifikasi dan rekomendasi klinis ditampilkan dalam hitungan detik.' },
  { n: '05', t: 'Rekomendasi Klinis',    d: 'Setiap hasil dilengkapi langkah penanganan awal yang dapat dipahami orang tua.' },
  { n: '06', t: 'Hanya Alat Bantu',      d: 'Dirancang sebagai skrining awal — tidak menggantikan diagnosis dokter spesialis.' },
];

export default function Home({ go }) {
  const statsRef = useFadeIn();
  const typesRef = useFadeIn();
  const featRef  = useFadeIn();
  const ctaRef   = useFadeIn();

  return (
    <div className="home">

      {/* ── Hero ── */}
      <section className="hero iso-grid-bg">

        {/* Animasi melayang bebas — bentuk pastel lembut bertema klinis */}
        <div className="hero-anim" aria-hidden="true">
          <span className="float-shape fs-lavender fs-1" />
          <span className="float-shape fs-salmon fs-2" />
          <span className="float-shape fs-yellow fs-3" />
          <span className="float-shape fs-mint fs-4" />
          <span className="float-shape fs-lavender fs-5" />
          <span className="float-shape fs-salmon fs-6" />
          <span className="float-ring fr-1" />
          <span className="float-ring fr-2" />
          <span className="float-ring fr-3" />
          <span className="float-plus fp-1">+</span>
          <span className="float-plus fp-2">+</span>
          <span className="float-plus fp-3">+</span>
          <span className="float-plus fp-4">+</span>
          {/* Partikel titik kecil naik */}
          {Array.from({length:12}).map((_,i)=>(
            <span key={i} className="particle" style={{
              left: `${8+i*7.5}%`,
              animationDelay: `${i*0.7}s`,
              animationDuration: `${6+Math.random()*5}s`,
            }}/>
          ))}
          {/* EKG garis denyut */}
          <svg className="float-pulse fp-left" viewBox="0 0 240 60" fill="none">
            <polyline
              points="0,30 40,30 55,30 65,8 80,52 95,30 130,30 145,30 155,16 168,44 180,30 240,30"
              stroke="var(--c-simple)" strokeWidth="2.5"
              strokeLinejoin="round" strokeLinecap="round" />
          </svg>
          <svg className="float-pulse fp-right" viewBox="0 0 240 60" fill="none">
            <polyline
              points="0,30 30,30 45,30 55,12 70,48 85,30 120,30 135,30 148,18 160,42 175,30 240,30"
              stroke="var(--c-afebrile)" strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
          </svg>
          {/* Gelombang halus bawah */}
          <svg className="hero-wave" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,80 C360,20 720,100 1080,40 C1260,10 1380,60 1440,50 L1440,120 L0,120Z" fill="var(--bg)" opacity="0.6"/>
            <path d="M0,90 C300,40 600,110 900,50 C1100,20 1300,70 1440,60 L1440,120 L0,120Z" fill="var(--bg)" opacity="0.4"/>
          </svg>
          {/* Siluet anak kecil di kanan (diam, dekoratif) */}
          <img src={`${process.env.PUBLIC_URL}/siluet/siluet_hijau.png`}  className="child-silhouette cs-1" alt="" aria-hidden="true" />
          <img src={`${process.env.PUBLIC_URL}/siluet/siluet_kuning.png`} className="child-silhouette cs-2" alt="" aria-hidden="true" />
          <img src={`${process.env.PUBLIC_URL}/siluet/siluet_salmon.png`} className="child-silhouette cs-3" alt="" aria-hidden="true" />
          <img src={`${process.env.PUBLIC_URL}/siluet/siluet_ungu.png`}   className="child-silhouette cs-4" alt="" aria-hidden="true" />
        </div>

        <div className="hero-body">
          <div className="hero-eyebrow">
            ANAK · KLINIS · MACHINE LEARNING
          </div>
          <h1 className="hero-title">
            Skrining kejang anak yang<br/>
            <span className="hl hl-lavender">Cepat,</span>{' '}
            <span className="hl hl-salmon">Akurat,</span>{' '}
            <span className="hl hl-yellow">Klinis.</span>
          </h1>
          <p className="hero-sub">
            Masukkan data usia, suhu tubuh, dan kondisi kejang anak. Sistem akan mengklasifikasikan
            kondisi sebagai <strong>demam sederhana</strong>, <strong>demam kompleks</strong>, atau
            <strong> tanpa demam</strong> — lengkap dengan rekomendasi klinis.
          </p>
          <div className="hero-btns">
            <button className="btn-primary lg" onClick={() => go('diagnosis')}>
              Mulai Diagnosis Gratis
            </button>
            <button className="btn-ghost lg" onClick={() => go('about')}>
              Lihat Performa Model
            </button>
          </div>
          <p className="hero-disclaimer">Alat bantu skrining awal — bukan pengganti diagnosis medis.</p>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="stats-row fade-section" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            {STATS.map((s, i) => (
              <div className="stat" key={i}>
                <div className="stat-v">{s.v}</div>
                <div className="stat-l">{s.l}</div>
                <div className="stat-s">{s.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Types ── */}
      <section className="types-section fade-section" ref={typesRef}>
        <div className="container">
          <div className="sec-head">
            <div className="eyebrow">Tiga Kategori</div>
            <h2>
              Kondisi kejang yang sistem ini{' '}
              <span className="hl hl-yellow">klasifikasikan.</span>
            </h2>
            <p>Berdasarkan gejala, suhu, durasi, dan pola kejadian — model membedakan tiga kelompok utama.</p>
          </div>
          <div className="types-grid">
            {TYPES.map(t => (
              <div className="type-card" key={t.key}
                style={{ '--tc': t.color, '--tbg': t.bg, '--tbd': t.border }}>
                <span className={`type-badge hl ${t.hl}`}>{t.badge}</span>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
                <ul>
                  {t.items.map((item, i) => (
                    <li key={i}><span className="dot"/>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="feat-section fade-section" ref={featRef}>
        <div className="container">
          <div className="sec-head">
            <div className="eyebrow">Fitur Sistem</div>
            <h2>Enam pilar yang mendukung <span className="hl hl-mint">deteksi dini.</span></h2>
          </div>
          <div className="feat-grid">
            {FEATURES.map((f, i) => (
              <div className="feat-card" key={i}>
                <div className="feat-n">{f.n}</div>
                <h4>{f.t}</h4>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section fade-section" ref={ctaRef}>
        <div className="container">
          <div className="cta-box iso-grid-bg">
            <div className="cta-inner">
              <h2>
                Mulai skrining <span className="hl hl-salmon">sekarang.</span>
              </h2>
              <p>Isi data gejala anak dan dapatkan hasil klasifikasi beserta panduan tindakan awal.</p>
              <div className="cta-btns">
                <button className="btn-primary lg" onClick={() => go('diagnosis')}>
                  Mulai Diagnosis →
                </button>
                <button className="btn-ghost lg" onClick={() => go('about')}>
                  Tentang Model
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
